import React, { useEffect, useState } from "react";
import { Lock, Type, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Page, Card, PrimaryButton } from "../components/UI";
import api from "../lib/api";

const GROUPS = [
  { title: "Homepage — hero", keys: [["hero_badge", "Badge"], ["hero_title_1", "Headline line 1"], ["hero_title_2", "Headline line 2 (italic, teal)"], ["hero_title_3", "Headline line 3"], ["hero_subtitle", "Subheadline", true], ["hero_cta_primary", "Main button"], ["hero_cta_secondary", "Second button"]] },
  { title: "Homepage — closing section", keys: [["final_title", "Heading"], ["final_subtitle", "Text", true], ["final_cta", "Button"]] },
  { title: "Coming Soon page", keys: [["coming_soon_title", "Heading"], ["coming_soon_message", "Message", true]] },
];
const field = "w-full px-4 py-2.5 rounded-xl text-sm outline-none";

export default function SiteEditor() {
  const [s, setS] = useState(null);
  const [content, setContent] = useState({});
  const [pw, setPw] = useState("");
  const load = () => api.get("/admin/site").then(({ data }) => { setS(data); setContent(data.content); });
  useEffect(() => { load(); }, []);

  const save = async (payload, msg) => {
    try { const { data } = await api.put("/admin/site", payload); setS(data); setContent(data.content); toast.success(msg); }
    catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Save failed"); }
  };
  if (!s) return <div className="mf-skeleton h-60" />;

  return (
    <Page title="Site Editor" subtitle="Change website wording and control the password-protected Coming Soon page.">
      <Card delay={0.05} className="mb-5" testid="site-gate-card">
        <h3 className="font-serif text-xl mb-3 flex items-center gap-2"><Lock className="w-5 h-5" style={{ color: "var(--mf-accent)" }} /> Coming Soon mode</h3>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <button onClick={() => save({ coming_soon_enabled: !s.coming_soon_enabled }, s.coming_soon_enabled ? "Site is now public" : "Coming Soon page is on")}
            className="flex items-center gap-3 text-sm" data-testid="coming-soon-toggle">
            <span className="w-11 h-6 rounded-full relative" style={{ background: s.coming_soon_enabled ? "var(--mf-primary)" : "#ccc" }}>
              <span className="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform" style={{ transform: s.coming_soon_enabled ? "translateX(22px)" : "translateX(2px)" }} />
            </span>
            <span data-testid="coming-soon-state">{s.coming_soon_enabled ? "On — visitors need the site password" : "Off — site is public"}</span>
          </button>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 max-w-lg">
          <input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New site password (6+ characters)" className={field} style={{ background: "var(--mf-muted)" }} data-testid="site-new-password" />
          <PrimaryButton onClick={() => { save({ password: pw }, "Site password changed — everyone must re-enter it"); setPw(""); }} disabled={pw.length < 6} testid="site-password-save">Change password</PrimaryButton>
        </div>
      </Card>

      {GROUPS.map((g, gi) => (
        <Card key={g.title} delay={0.1 + gi * 0.05} className="mb-5" testid={`site-group-${gi}`}>
          <h3 className="font-serif text-xl mb-4 flex items-center gap-2"><Type className="w-5 h-5" style={{ color: "var(--mf-accent)" }} /> {g.title}</h3>
          <div className="space-y-3">
            {g.keys.map(([k, label, long]) => (
              <label key={k} className="block">
                <span className="text-xs font-medium flex items-center justify-between" style={{ color: "var(--mf-text-muted)" }}>
                  {label}
                  {content[k] !== s.defaults[k] && <button type="button" onClick={() => setContent({ ...content, [k]: s.defaults[k] })} className="flex items-center gap-1" style={{ color: "var(--mf-primary)" }} data-testid={`site-reset-${k}`}><RotateCcw className="w-3 h-3" /> Default</button>}
                </span>
                {long ? <textarea rows={3} value={content[k] || ""} onChange={(e) => setContent({ ...content, [k]: e.target.value })} className={`${field} mt-1`} style={{ background: "var(--mf-muted)" }} data-testid={`site-field-${k}`} />
                  : <input value={content[k] || ""} onChange={(e) => setContent({ ...content, [k]: e.target.value })} className={`${field} mt-1`} style={{ background: "var(--mf-muted)" }} data-testid={`site-field-${k}`} />}
              </label>
            ))}
          </div>
        </Card>
      ))}
      <div className="sticky bottom-24 md:bottom-6 flex justify-end">
        <PrimaryButton onClick={() => save({ content }, "Wording saved — live on the site now")} testid="site-content-save">Save wording</PrimaryButton>
      </div>
    </Page>
  );
}
